import React from 'react';
import { scaleLinear, scaleSequential } from 'd3-scale';
import { interpolateViridis } from 'd3-scale-chromatic';
import { FloorPriceData } from '../../types';

interface ScatterPlotProps {
  data: FloorPriceData[];
}

// Linear regression calculation helper
function calculateLinearRegression(data: FloorPriceData[]) {
  const n = data.length;
  if (n < 2) return null;
  
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  
  data.forEach(d => {
    const x = Number(d.unit_per_floor);
    const y = Number(d.median_price_per_m2);
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  });
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  return { slope, intercept };
}

export const ScatterPlot: React.FC<ScatterPlotProps> = ({ data }) => {
  const chartWidth = 500;
  const chartHeight = 400;
  const margin = { top: 20, right: 40, bottom: 60, left: 80 };
  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;

  // Handle empty data
  if (!data || data.length === 0) {
    return (
      <div className="scatter-plot-container">
        <h3>Median Price per m² by Floor Level</h3>
        <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0aec0' }}>
          No floor price data available
        </div>
      </div>
    );
  }

  const xExtent = [Math.min(...data.map(d => Number(d.unit_per_floor))), Math.max(...data.map(d => Number(d.unit_per_floor)))];
  const yExtent = [Math.min(...data.map(d => Number(d.median_price_per_m2))), Math.max(...data.map(d => Number(d.median_price_per_m2)))];
  
  const xScale = scaleLinear()
    .domain(xExtent)
    .range([0, innerWidth]);
    
  const yScale = scaleLinear()
    .domain(yExtent)
    .range([innerHeight, 0]);

  const colorScale = scaleSequential(interpolateViridis)
    .domain(yExtent);
    
  // Size scale based on listing count
  const sizeScale = scaleLinear()
    .domain([Math.min(...data.map(d => Number(d.listing_count))), Math.max(...data.map(d => Number(d.listing_count)))])
    .range([4, 12]);
    
  const regression = calculateLinearRegression(data);

  return (
    <div className="scatter-plot-container">
      <h3>Median Price per m² by Floor Level ({data.length} floor levels)</h3>
      <svg width={chartWidth} height={chartHeight} style={{ display: 'block', margin: '0 auto' }}>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {/* Regression line */}
          {regression && (
            <line
              x1={0}
              y1={yScale(regression.intercept + regression.slope * xExtent[0])}
              x2={innerWidth}
              y2={yScale(regression.intercept + regression.slope * xExtent[1])}
              stroke="#e74c3c"
              strokeWidth="2"
              opacity={0.8}
            />
          )}
          
          {/* Data points */}
          {data.map((d, i) => (
            <circle
              key={i}
              cx={xScale(Number(d.unit_per_floor))}
              cy={yScale(Number(d.median_price_per_m2))}
              r={sizeScale(Number(d.listing_count))}
              fill={colorScale(Number(d.median_price_per_m2))}
              opacity={0.7}
              stroke="white"
              strokeWidth="1"
            >
              <title>{`Floor: ${Number(d.unit_per_floor)}, Median Price: R$${Number(d.median_price_per_m2).toFixed(2)}/m², Listings: ${Number(d.listing_count)}`}</title>
            </circle>
          ))}
          
          {/* X axis */}
          <g transform={`translate(0, ${innerHeight})`}>
            <line x1={0} x2={innerWidth} stroke="#4a5568" />
            {xScale.ticks(8).map(tick => (
              <g key={tick} transform={`translate(${xScale(tick)}, 0)`}>
                <line y2={6} stroke="#4a5568" />
                <text y={20} textAnchor="middle" fontSize="12" fill="#a0aec0" fontWeight="500">
                  {tick}
                </text>
              </g>
            ))}
            <text x={innerWidth / 2} y={50} textAnchor="middle" fontSize="14" fill="#e2e8f0" fontWeight="600">
              Floor Level
            </text>
          </g>
          
          {/* Y axis */}
          <g>
            <line y1={0} y2={innerHeight} stroke="#4a5568" />
            {yScale.ticks(6).map(tick => (
              <g key={tick} transform={`translate(0, ${yScale(tick)})`}>
                <line x2={-6} stroke="#4a5568" />
                <text x={-10} textAnchor="end" dy="0.35em" fontSize="12" fill="#a0aec0" fontWeight="500">
                  R${tick}
                </text>
              </g>
            ))}
            <text x={-60} y={innerHeight / 2} textAnchor="middle" fontSize="14" fill="#e2e8f0" fontWeight="600" transform={`rotate(-90, -60, ${innerHeight / 2})`}>
              Price per m²
            </text>
          </g>
          
          {/* Regression equation */}
          {regression && (
            <text x={10} y={20} fontSize="12" fill="#e74c3c" fontWeight="600">
              y = {regression.slope.toFixed(3)}x + {regression.intercept.toFixed(2)}
            </text>
          )}
        </g>
      </svg>
      <div className="mini-legend">
        <span className="legend-square" style={{ background: '#fde725' }} /> Higher price/m²
        <span style={{ opacity: 0.6 }}>•</span>
        <span className="legend-square" style={{ background: '#440154' }} /> Lower price/m²
      </div>
      {regression && (
        <div style={{ marginTop: '5px', fontSize: '12px', color: '#a0aec0', textAlign: 'center' }}>
          <strong>Linear Regression:</strong> y = {regression.slope.toFixed(3)}x + {regression.intercept.toFixed(2)} 
          {regression.slope > 0 ? 
            ` (${(regression.slope * 10).toFixed(2)} R$ increase per 10 floors)` : 
            ` (${Math.abs(regression.slope * 10).toFixed(2)} R$ decrease per 10 floors)`
          }
        </div>
      )}
    </div>
  );
};
